import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const Terms = () => {
  return (
    <div className="pt-16" data-testid="terms-page">
      <SEO 
        title="Terms of Service - Conditions of Use"
        description="Read the Terms of Service for Turcoaz Apps and Smart PDF Tools. Understand your rights, responsibilities, and our service conditions for using our document processing tools."
        canonical="/terms"
      />

      {/* Header */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Legal
            </p>
            <h1 
              className="text-4xl sm:text-5xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Terms of Service
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              Last updated: January 2025
            </p>
            <p className="text-slate-600 leading-relaxed">
              Please read these Terms of Service carefully before using our services. By accessing or using Turcoaz Apps, you agree to be bound by these terms.
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-slate-500 mb-3">On this page</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
            <span className="text-slate-600">1. Acceptance of Terms</span>
            <span className="text-slate-600">9. Third-Party Services</span>
            <span className="text-slate-600">2. Description of Services</span>
            <span className="text-slate-600">10. Accounts &amp; Payments</span>
            <span className="text-slate-600">3. User Responsibilities</span>
            <span className="text-slate-600">11. Termination</span>
            <span className="text-slate-600">4. Acceptable Use</span>
            <span className="text-slate-600">12. Changes to Terms</span>
            <span className="text-slate-600">5. Intellectual Property</span>
            <span className="text-slate-600">13. Governing Law</span>
            <span className="text-slate-600">6. Disclaimers</span>
            <span className="text-slate-600">14. Indemnification</span>
            <span className="text-slate-600">7. Limitation of Liability</span>
            <span className="text-slate-600">15. Severability</span>
            <span className="text-slate-600">8. Service Availability</span>
            <span className="text-slate-600">16. Contact Information</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            {/* 1. Acceptance of Terms */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              1. Acceptance of Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These Terms of Service ("Terms," "Terms of Service," or "Conditions of Use") constitute a legally binding agreement between you ("User," "you," or "your") and Turcoaz Apps ("Company," "we," "us," or "our") governing your access to and use of the turcoazapps.com website, pdf.turcoazapps.com, and all related services, applications, and tools (collectively, the "Services").
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use our Services.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              If you are using our Services on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms. In such cases, "you" and "your" will refer to both you individually and the organization you represent.
            </p>

            {/* 2. Description of Services */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              2. Description of Services
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Turcoaz Apps provides online software tools designed to help users work with documents and improve productivity. Our Services include, but are not limited to:
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              2.1 PDF Processing Tools
            </h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Our Smart PDF Tools platform offers a suite of document processing capabilities:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Merge PDF:</strong> Combine multiple PDF files into a single document</li>
              <li><strong>Split PDF:</strong> Divide PDF files into separate documents by page or range</li>
              <li><strong>Compress PDF:</strong> Reduce PDF file size while maintaining quality</li>
              <li><strong>Convert PDF:</strong> Transform documents between PDF and other formats</li>
              <li><strong>Edit PDF:</strong> Modify, annotate, and manipulate PDF content</li>
              <li><strong>Organize PDF:</strong> Reorder, rotate, and delete pages within PDF files</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              2.2 AI-Powered Tools
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may offer artificial intelligence features that provide automated document analysis, content generation, or other AI-assisted functionality. These tools are designed to augment user productivity but are subject to the limitations inherent in current AI technology.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>Important:</strong> AI-generated outputs are provided for informational and productivity purposes only. We do not guarantee the accuracy, completeness, or reliability of any AI-generated content. Users are solely responsible for reviewing, verifying, and validating any AI outputs before use in critical applications.
            </p>

            {/* 3. User Responsibilities */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              3. User Responsibilities
            </h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              As a user of our Services, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Provide accurate information when creating an account or contacting us</li>
              <li>Maintain the confidentiality of any account credentials</li>
              <li>Use our Services only for lawful purposes and in compliance with applicable laws</li>
              <li>Ensure you have the legal right to upload, process, and modify any documents you submit</li>
              <li>Take responsibility for maintaining backup copies of your original documents</li>
              <li>Review and verify all processed outputs before relying on them for important purposes</li>
              <li>Report any security vulnerabilities or bugs you discover to us promptly</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              You are solely responsible for all activities conducted through your use of our Services, including any documents you upload, process, or download.
            </p>

            {/* 4. Acceptable Use */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              4. Acceptable Use Policy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              You agree not to use our Services to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Upload, process, or distribute content that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
              <li>Process documents containing child sexual abuse material (CSAM) or any content that exploits minors</li>
              <li>Infringe upon or violate the intellectual property rights, privacy rights, or other rights of any third party</li>
              <li>Distribute malware, viruses, or any other malicious code or files</li>
              <li>Attempt to gain unauthorized access to our systems, networks, or other users' accounts</li>
              <li>Interfere with or disrupt the integrity or performance of our Services</li>
              <li>Use automated systems, bots, or scripts to access our Services without our express written permission</li>
              <li>Circumvent, disable, or otherwise interfere with security-related features of our Services</li>
              <li>Engage in any activity that could damage, overburden, or impair our infrastructure</li>
              <li>Use our Services for any fraudulent, deceptive, or illegal purpose</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We reserve the right to investigate and take appropriate legal action against anyone who violates this Acceptable Use Policy, including removing content, suspending access, and reporting violations to law enforcement authorities.
            </p>

            {/* 5. Intellectual Property */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              5. Intellectual Property Rights
            </h2>
            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              5.1 Our Intellectual Property
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Services, including all content, features, functionality, software, designs, text, graphics, logos, and trademarks, are owned by Turcoaz Apps or our licensors and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may not copy, modify, distribute, sell, lease, or create derivative works based on our Services or any part thereof without our express written consent.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              5.2 Your Content
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              You retain all ownership rights to the documents and content you upload to our Services. By uploading content, you grant us a limited, non-exclusive, royalty-free license to process your content solely for the purpose of providing the requested Services to you.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              This license terminates when your content is deleted from our systems (which occurs automatically after processing). We do not claim any ownership rights to your content.
            </p>

            {/* 6. Disclaimer of Warranties */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              6. Disclaimer of Warranties
            </h2>
            <div className="bg-slate-50 border-l-4 border-slate-300 p-4 mb-4">
              <p className="text-slate-600 leading-relaxed font-medium">
                OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed mb-3">
              To the fullest extent permitted by applicable law, we disclaim all warranties, express or implied, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
              <li>Warranties that the Services will be uninterrupted, timely, secure, or error-free</li>
              <li>Warranties regarding the accuracy, reliability, or completeness of any content or results</li>
              <li>Warranties that defects will be corrected or that our Services are free of viruses or harmful components</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>AI Output Disclaimer:</strong> We make no representations or warranties regarding the accuracy, completeness, or suitability of any AI-generated content. AI outputs may contain errors, inaccuracies, or inappropriate content. You acknowledge that AI technology has inherent limitations and agree to independently verify any AI-generated results before relying on them.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you. In such cases, our liability will be limited to the maximum extent permitted by applicable law.
            </p>

            {/* 7. Limitation of Liability */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              7. Limitation of Liability
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              To the maximum extent permitted by applicable law, Turcoaz Apps and its officers, directors, employees, agents, suppliers, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Loss of goodwill or reputation</li>
              <li>Cost of procurement of substitute services</li>
              <li>Any damages arising from your use of or inability to use our Services</li>
              <li>Any damages arising from unauthorized access to or alteration of your transmissions or data</li>
              <li>Any damages arising from errors, inaccuracies, or omissions in AI-generated content</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              In no event shall our total liability to you for all claims arising from or related to your use of the Services exceed the greater of: (a) the amount you paid us, if any, in the twelve (12) months preceding the claim, or (b) one hundred US dollars ($100).
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              These limitations apply regardless of the legal theory on which the claim is based, whether arising from contract, tort, negligence, strict liability, or any other basis, even if we have been advised of the possibility of such damages.
            </p>

            {/* 8. Service Availability */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              8. Service Availability
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We strive to maintain high availability of our Services, but we do not guarantee uninterrupted access. Our Services may be temporarily unavailable due to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Scheduled maintenance and updates</li>
              <li>Emergency repairs or security patches</li>
              <li>Technical failures or infrastructure issues</li>
              <li>Events beyond our reasonable control (force majeure)</li>
              <li>High demand or capacity limitations</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We reserve the right to modify, suspend, or discontinue any part of our Services at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Services.
            </p>

            {/* 9. Third-Party Services */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              9. Third-Party Services
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our Services may integrate with or contain links to third-party websites, services, or content that are not owned or controlled by Turcoaz Apps. This includes, but is not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Cloud infrastructure and hosting providers</li>
              <li>Payment processing services</li>
              <li>AI and machine learning service providers</li>
              <li>Analytics and advertising services</li>
              <li>External websites linked from our platform</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party services. Your use of third-party services is governed by their respective terms and policies. We encourage you to review the terms and privacy policies of any third-party services you access.
            </p>

            {/* 10. Account and Credits */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              10. Accounts, Payments, and Credits
            </h2>
            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              10.1 Account Registration
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some features of our Services may require you to create an account. When registering, you agree to provide accurate, current, and complete information and to update this information as necessary. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              10.2 Paid Services
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Certain features or enhanced capabilities may be offered as paid services. By purchasing paid services, you agree to pay all applicable fees as described at the time of purchase. All fees are non-refundable unless otherwise stated or required by applicable law.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              10.3 Credits and Usage Limits
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              We may implement credit-based systems or usage limits for certain features. Credits may expire if not used within a specified period. We reserve the right to modify credit values, expiration policies, and usage limits at any time with reasonable notice.
            </p>

            {/* 11. Termination */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              11. Termination
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We reserve the right to suspend or terminate your access to our Services at any time, with or without cause, and with or without notice. Grounds for termination may include, but are not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Violation of these Terms of Service</li>
              <li>Violation of our Acceptable Use Policy</li>
              <li>Conduct that we determine, in our sole discretion, is harmful to other users, us, or third parties</li>
              <li>Requests by law enforcement or government agencies</li>
              <li>Extended periods of inactivity</li>
              <li>Discontinuation of the Services</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Upon termination, your right to use the Services will immediately cease. Sections of these Terms that by their nature should survive termination shall survive, including but not limited to intellectual property provisions, disclaimers, limitations of liability, and dispute resolution provisions.
            </p>

            {/* 12. Changes to Terms */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              12. Changes to These Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. When we make changes, we will:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Update the "Last updated" date at the top of these Terms</li>
              <li>Post the revised Terms on this page</li>
              <li>For material changes, provide notice through our website or by email (if you have provided one)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Your continued use of our Services after any changes to these Terms constitutes your acceptance of the revised Terms. If you do not agree to the modified Terms, you must stop using our Services.
            </p>

            {/* 13. Governing Law */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              13. Governing Law and Dispute Resolution
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. Any disputes arising out of or relating to these Terms or your use of our Services shall be resolved through the following process:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Informal Resolution:</strong> Before initiating formal proceedings, you agree to contact us first to attempt to resolve any dispute informally.</li>
              <li><strong>Binding Arbitration:</strong> If informal resolution fails, disputes shall be resolved through binding arbitration in accordance with applicable arbitration rules.</li>
              <li><strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Notwithstanding the above, we retain the right to seek injunctive or other equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of our intellectual property rights.
            </p>

            {/* 14. Indemnification */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              14. Indemnification
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless Turcoaz Apps and its officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Your use of or access to our Services</li>
              <li>Your violation of these Terms of Service</li>
              <li>Your violation of any third-party rights, including intellectual property or privacy rights</li>
              <li>Content you upload, process, or transmit through our Services</li>
              <li>Any claim that content you uploaded caused damage to a third party</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              This indemnification obligation will survive the termination of your use of our Services and these Terms.
            </p>

            {/* 15. Severability and Entire Agreement */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              15. Severability and Entire Agreement
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              These Terms, together with our <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</Link> and <Link to="/cookie-policy" className="text-teal-600 hover:text-teal-700">Cookie Policy</Link>, constitute the entire agreement between you and Turcoaz Apps regarding your use of our Services, superseding any prior agreements or understandings.
            </p>

            {/* 16. Contact Information */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              16. Contact Information
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have any questions, concerns, or feedback regarding these Terms of Service, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-600 mb-2">
                <strong>Email:</strong>{' '}
                <a href="mailto:contact@turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  contact@turcoazapps.com
                </a>
              </p>
              <p className="text-slate-600 mb-2">
                <strong>Contact Form:</strong>{' '}
                <Link to="/contact" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com/contact
                </Link>
              </p>
              <p className="text-slate-600">
                <strong>Website:</strong>{' '}
                <a href="https://turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com
                </a>
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed">
              For legal inquiries, please include "Legal Inquiry" in your subject line to ensure proper routing of your message.
            </p>

          </div>
        </div>
      </section>
    </div>
  );
};
